import { Link } from 'react-router-dom'

import { LEFT_SUFFIX, nameFor } from '../directory.js'

// One person, named through the one join site. P6-186.
//
// 🔴 LIFTED OUT OF `MoviePanel.jsx` RATHER THAN COPIED INTO `MusicPanel.jsx`,
// and the reason is the one P6-177 already paid for on this page: *"one fact,
// one spelling"*. `rotation_position` shipped in three strings and read as two
// different facts on one screen. A second copy of this component would be the
// same shape — two panels each deciding how a departed member is drawn, and the
// `(left the server)` suffix appearing with a comma in one of them the first
// time anybody edits either.
//
// It is four lines of JSX. That is exactly the size of thing that gets copied.

export default function Person({ directory, userId }) {
  const person = nameFor(directory, typeof userId === 'string' ? userId : null)
  return (
    <>
      {person.label}
      {person.left ? <span className="d2d-portal__left"> ({LEFT_SUFFIX})</span> : null}
    </>
  )
}

/**
 * Several people, in a sentence. P6-182.
 *
 * 🔴 LIFTED OUT OF `MusicPanel.jsx`'s `InductedBy` FOR THE REASON THE HEADER
 * ALREADY GIVES, one consumer later: the activity feed names the members who
 * put a track in the canon, and that is the same sentence the panel's
 * provenance line draws. Two copies would be two answers to *"where does the
 * comma go and where does the 'and' go"*, which is P6-177's exact shape.
 *
 * ⚠️ `ids` MUST ALREADY BE JOINABLE. Nothing here coerces — a snowflake that
 * arrived as a JSON number does not survive the browser's parse and
 * `String()`-ing it produces a well-formed id belonging to nobody, or to
 * somebody else. `music.js::joinableId` is the one gate, and both callers pass
 * their ids through it before they get here.
 */
export function People({ directory, ids }) {
  const list = Array.isArray(ids) ? ids : []
  return (
    <>
      {list.map((id, index) => (
        <span key={id}>
          {index === 0 ? null : index === list.length - 1 ? ' and ' : ', '}
          <Person directory={directory} userId={id} />
        </span>
      ))}
    </>
  )
}

/**
 * V2-2c: wraps `children` in a `Link` to `to`, or in a plain, unlinked
 * `<span>` carrying the SAME `className`/`testId` when `to` is falsy. This
 * is the ONE place a member chip or avatar decides whether it can navigate
 * anywhere at all — every caller computes `to` itself (typically
 * `watch.js::memberProfilePath(id)`, which already returns `null` for an
 * id `joinableId` would refuse), so this component stays feature-agnostic:
 * it never reads a route table, never calls `joinableId`, and never
 * assumes the destination is a watch profile.
 *
 * `className`/`testId` are carried by BOTH branches deliberately — a
 * pre-existing test asserting a chip's own class (the "you" modifier, say)
 * or its testid must keep seeing exactly one element either way, never a
 * bare, unwrapped `children` that silently drops both the moment an id
 * happens to be unresolvable. `to` is the only thing that changes; the
 * element a caller's existing markup and tests already depend on does not.
 *
 * Not a second identity-resolution site (P6-186's own reason this file
 * exists): the caller has already resolved the person via `nameFor` for
 * the avatar/name it is about to wrap, and this component reads nothing
 * about WHO is being linked, only WHERE.
 */
export function MemberLink({ to, className, testId = 'member-link', children, ...rest }) {
  if (!to) {
    return (
      <span className={className} data-testid={testId} {...rest}>
        {children}
      </span>
    )
  }
  return (
    <Link to={to} className={className} data-testid={testId} {...rest}>
      {children}
    </Link>
  )
}
